import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { Ban, ShieldAlert, Activity } from "lucide-react";
import UserNameLink from "./UserNameLink";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function isToday(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
}

/**
 * Compact horizontal strip at the top of /admin/users showing today's
 * moderation actions (bans + unbans) so mods can see at a glance what
 * their team has been doing. Pulls from the existing ban_audit_log endpoint.
 */
export default function ModActivityStrip() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/ban-audit", { params: { limit: 20 } });
      // Only today's events for the strip
      const today = (data.items || []).filter((it) => isToday(it.created_at));
      setItems(today);
    } catch {
      // silent — 403 for non-staff
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  if (loading || items.length === 0) return null;

  const bans = items.filter((i) => i.action === "ban").length;
  const unbans = items.filter((i) => i.action === "unban").length;

  return (
    <div
      className="border border-slate-200 bg-white p-3 mb-6 flex items-start gap-3"
      data-testid="mod-activity-strip"
    >
      <div className="shrink-0 flex items-center gap-2 pr-3 border-r border-slate-200 min-w-[130px]">
        <Activity className="w-4 h-4 text-[#0D9488]" />
        <div>
          <div className="font-mono-num text-[9px] uppercase tracking-[0.3em] text-[#0D9488]">// Today</div>
          <div className="font-mono-num text-[11px] text-slate-600 uppercase tracking-widest">
            <span className="text-orange-400">{bans} ban{bans === 1 ? "" : "s"}</span>
            <span className="mx-1 text-zinc-700">·</span>
            <span className="text-emerald-400">{unbans} unban{unbans === 1 ? "" : "s"}</span>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-x-auto">
        <ul className="flex items-center gap-2" data-testid="mod-activity-list">
          {items.map((it) => {
            const isBan = it.action === "ban";
            return (
              <li
                key={it.id}
                className={`shrink-0 flex items-center gap-2 px-2.5 py-1.5 border text-[11px] font-mono-num whitespace-nowrap ${
                  isBan
                    ? "border-orange-500/30 bg-orange-500/5 text-orange-400"
                    : "border-emerald-500/30 bg-emerald-500/5 text-emerald-400"
                }`}
                data-testid={`mod-activity-item-${it.id}`}
                title={it.reason || (isBan ? "No reason provided" : "")}
              >
                {isBan ? <Ban className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />}
                <span className="text-slate-500 uppercase tracking-widest text-[9px]">{it.actor_name}</span>
                <span className="text-zinc-700">→</span>
                <UserNameLink
                  userId={it.target_user_id}
                  name={it.target_name || it.target_email}
                  className="text-slate-900"
                  showUnderline={false}
                  testid={`mod-activity-target-${it.id}`}
                />
                <span className="text-slate-400 uppercase tracking-widest text-[9px]">· {timeAgo(it.created_at)}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
